import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from './storage';

const AUTH_EXEMPT_URL_PATTERNS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgotPass',
  '/auth/verifyOtp',
  '/auth/resetPassword',
  '/auth/refresh',
  '/auth/logout',
];

const MEDIA_EXEMPT_URL_PATTERNS = [
  '/photo/',
  '/studentPhoto',
  '/applicationPhoto',
  '/signature/',
  '/studentSignature',
  '/applicationSignature',
];

const getRequestUrl = (config?: InternalAxiosRequestConfig): string => {
  if (!config?.url) return '';
  return config.url;
};

export const isExemptUrl = (url: string): boolean => {
  if (!url) return false;
  return (
    AUTH_EXEMPT_URL_PATTERNS.some((pattern) => url.includes(pattern)) ||
    MEDIA_EXEMPT_URL_PATTERNS.some((pattern) => url.includes(pattern))
  );
};

const hadBearerTokenOnRequest = (config?: InternalAxiosRequestConfig): boolean => {
  if (!config?.headers) return false;
  const headers = config.headers as Record<string, string | undefined>;
  const authHeader = headers.Authorization ?? headers['Authorization'];
  return typeof authHeader === 'string' && authHeader.startsWith('Bearer ');
};

/**
 * Returns true only when a 401 likely means the stored JWT is invalid or expired,
 * not a generic authorization failure on a specific resource.
 */
export const shouldLogoutOnUnauthorizedResponse = (error: unknown): boolean => {
  const axiosError = error as AxiosError;
  if (axiosError?.response?.status !== 401) return false;

  const url = getRequestUrl(axiosError.config);
  if (isExemptUrl(url)) return false;

  const tokenInStorage = getAuthToken();
  const sentBearer = hadBearerTokenOnRequest(axiosError.config);

  // Logout only when we believed we were authenticated (token stored or sent on request).
  return Boolean(tokenInStorage || sentBearer);
};

export const shouldLogoutOnFetchUnauthorized = (url: string): boolean => {
  if (isExemptUrl(url)) return false;
  return Boolean(getAuthToken());
};
